import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { api, ApiError } from '@/api/client'
import { registerPushToken } from '@/services/push'
import type { AuthResponse, Household, Membership, Role, Tokens, User } from '@/api/types'

/**
 * Jetonlar localStorage'da tutuluyor.
 *
 * Alternatif httpOnly cookie olurdu ve XSS'e karşı daha güvenli olurdu; ama
 * aynı API'yi mobil de kullanıyor ve orada cookie yok. Tek bir jeton modeli
 * tutmak iki ayrı oturum mekanizması yönetmekten yeterince değerli.
 * Karşılığında XSS'e karşı savunma CSP ve girdi kaçışına yükleniyor.
 */
const STORAGE_KEY = 'nakito.session'

/**
 * Tanıtım sayfasındaki dil düğmesinin yazdığı anahtar.
 *
 * PublicLayout ile PAYLAŞILIYOR; iki yerde ayrı ayrı yazılmış bir dize
 * olsaydı biri değişip diğeri unutulur ve seçim yine kaybolurdu.
 */
export const PUBLIC_LOCALE_KEY = 'nakito.locale'

interface StoredSession {
  accessToken: string
  refreshToken: string
  accessExpiresAt: string
  activeHouseholdId: string | null
  locale: string
}

function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as StoredSession) : null
  } catch {
    return null
  }
}

export const useAuthStore = defineStore('auth', () => {
  const stored = loadSession()

  const user = ref<User | null>(null)
  const household = ref<Household | null>(null)
  const memberships = ref<Membership[]>([])
  const role = ref<Role | null>(null)

  const accessToken = ref<string | null>(stored?.accessToken ?? null)
  const refreshToken = ref<string | null>(stored?.refreshToken ?? null)
  const accessExpiresAt = ref<string | null>(stored?.accessExpiresAt ?? null)
  const activeHouseholdId = ref<string | null>(stored?.activeHouseholdId ?? null)
  /**
   * Dil sırası: oturum profili → AÇIK SEÇİM → tarayıcı dili → tr.
   *
   * Ortadaki basamak eksikti. Tanıtım sayfasındaki dil düğmesi seçimi
   * 'nakito.locale' anahtarına YAZIYOR ama hiçbir yer okumuyordu:
   * Türkçe'yi seçen ziyaretçi sayfayı yenilediğinde tarayıcı diline
   * geri dönüyordu. Yazma tarafı ölü koddu ve bu, hatayı görünmez
   * kılıyordu — kod doğru şeyi yapıyormuş gibi duruyor.
   *
   * Profil ÜSTTE: giriş yapmış kullanıcının sunucudaki tercihi, o
   * tarayıcıda bir kez yapılmış bir seçimden daha bağlayıcı.
   */
  const locale = ref<string>(
    stored?.locale ??
      localStorage.getItem(PUBLIC_LOCALE_KEY) ??
      navigator.language.slice(0, 2) ??
      'tr',
  )

  /** Oturum açık mı — jeton var mı? Geçerliliğini sunucu söyler. */
  const isAuthenticated = computed(() => Boolean(accessToken.value))

  /** Salt okuma yetkisi olan üye yazma işlemlerini görmemelidir. */
  const canWrite = computed(() => role.value === 'owner' || role.value === 'admin' || role.value === 'member')

  function persist() {
    if (!accessToken.value || !refreshToken.value) {
      localStorage.removeItem(STORAGE_KEY)
      return
    }
    const session: StoredSession = {
      accessToken: accessToken.value,
      refreshToken: refreshToken.value,
      accessExpiresAt: accessExpiresAt.value ?? '',
      activeHouseholdId: activeHouseholdId.value,
      locale: locale.value,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  }

  function applyTokens(tokens: Tokens) {
    accessToken.value = tokens.access_token
    refreshToken.value = tokens.refresh_token
    accessExpiresAt.value = tokens.access_expires_at
    persist()
  }

  function applyAuth(response: AuthResponse) {
    user.value = response.user
    household.value = response.household
    role.value = response.role
    activeHouseholdId.value = response.household.id
    locale.value = response.user.locale || locale.value
    applyTokens(response.tokens)
  }

  function clearSession() {
    user.value = null
    household.value = null
    memberships.value = []
    role.value = null
    accessToken.value = null
    refreshToken.value = null
    accessExpiresAt.value = null
    activeHouseholdId.value = null
    localStorage.removeItem(STORAGE_KEY)
  }

  /** Cihaz bilgisi — sunucu oturumu cihaz başına yönetsin diye. */
  function deviceInfo() {
    let installId = localStorage.getItem('nakito.install_id')
    if (!installId) {
      installId = crypto.randomUUID()
      localStorage.setItem('nakito.install_id', installId)
    }
    return {
      install_id: installId,
      platform: 'web',
      model: navigator.userAgent.slice(0, 120),
    }
  }

  async function login(email: string, password: string) {
    const response = await api.post<AuthResponse>(
      '/auth/login',
      { email, password, device: deviceInfo() },
      { anonymous: true },
    )
    applyAuth(response)
    await loadMemberships()
    // Bildirim izni GİRİŞTEN SONRA ve BEKLENMEDEN: izin diyaloğu
    // beklenirse kullanıcı hâlâ giriş ekranında asılı kalırdı.
    // Mobilde de aynı sıra ve aynı sebep — orada tam olarak bu hata
    // yaşandı ve girişi kilitledi.
    void registerPushToken()
  }

  async function register(input: {
    email: string
    password: string
    name: string
    base_currency: string
  }) {
    const response = await api.post<AuthResponse>(
      '/auth/register',
      { ...input, locale: locale.value, device: deviceInfo() },
      { anonymous: true },
    )
    applyAuth(response)
    await loadMemberships()
  }

  /**
   * Jetonu yeniler. Başarısızsa oturum temizlenir.
   * İstemci katmanı bunu eşzamanlı çağrılara karşı tekilleştirir.
   */
  async function refresh(): Promise<boolean> {
    if (!refreshToken.value) return false
    try {
      const response = await api.post<{ tokens: Tokens }>(
        '/auth/refresh',
        { refresh_token: refreshToken.value },
        { anonymous: true },
      )
      applyTokens(response.tokens)
      return true
    } catch {
      clearSession()
      return false
    }
  }

  async function logout() {
    const token = refreshToken.value
    // Oturumu önce yerelde kapatıyoruz: sunucu erişilemezse bile kullanıcı
    // çıkış yapabilmeli. Sunucu tarafı iptal en iyi çabadır.
    clearSession()
    if (token) {
      try {
        await api.post('/auth/logout', { refresh_token: token }, { anonymous: true })
      } catch {
        /* çıkışta hata gösterilmez */
      }
    }
  }

  async function loadMemberships() {
    const response = await api.get<{ items: Membership[] }>('/households')
    memberships.value = response.items
  }

  /** Oturumu sunucudan tazeler — sayfa yenilendiğinde çağrılır. */
  async function hydrate(): Promise<boolean> {
    if (!accessToken.value) return false
    try {
      const response = await api.get<{ user: User; household_id: string; role: Role }>('/me')
      user.value = response.user
      role.value = response.role
      activeHouseholdId.value = response.household_id
      locale.value = response.user.locale || locale.value

      await loadMemberships()
      household.value =
        memberships.value.find((m) => m.household.id === response.household_id)?.household ?? null
      persist()
      return true
    } catch (error) {
      // Yalnızca kimlik hatalarında oturumu düşür. Ağ kesintisinde oturumu
      // silmek, uçakta uygulamayı açan kullanıcıyı gereksiz yere çıkarır.
      if (error instanceof ApiError && error.status === 401) {
        clearSession()
      }
      return false
    }
  }

  async function switchHousehold(householdId: string) {
    activeHouseholdId.value = householdId
    persist()

    const response = await api.get<{ household: Household; role: Role }>('/households/current')
    household.value = response.household
    role.value = response.role
  }

  function setLocale(next: string) {
    locale.value = next
    persist()
  }

  return {
    user,
    household,
    memberships,
    role,
    accessToken,
    refreshToken,
    activeHouseholdId,
    locale,
    isAuthenticated,
    canWrite,
    login,
    register,
    refresh,
    logout,
    hydrate,
    loadMemberships,
    switchHousehold,
    setLocale,
    clearSession,
  }
})
